import React, {
  CSSProperties,
  ImgHTMLAttributes,
  useState,
  useRef,
  useEffect,
} from 'react';
import IconExpand from './IconExpand';
import IconContract from './IconContract';
import styles from './ExpandableImage.module.css';

interface Dimensions {
  contracted: CSSProperties;
  expanded: CSSProperties;
}

const defaultDimensions: Dimensions = {
  expanded: {
    width: '100%',
    maxWidth: '19.5rem',
  },
  contracted: {
    width: '100%',
    maxWidth: '5rem',
  },
};

export interface ExpandableImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  expandAriaLabel: string;
  contractAriaLabel: string;
  dimensions?: Dimensions;
  featureName?: string;
  onContract?: () => void;
  onExpand?: () => void;
  onValidImageDetermined?: (isValid: boolean) => void;
}

const ExpandableImage = ({
  expandAriaLabel,
  contractAriaLabel,
  dimensions = defaultDimensions,
  onContract,
  onExpand,
  onValidImageDetermined,
  src,
  alt,
}: ExpandableImageProps) => {
  const [expanded, setExpanded] = useState(false);
  const img = useRef<HTMLImageElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleContract = () => {
    setExpanded(false);
  };

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleTransitionEnd = () => {
    const fn = expanded ? onExpand : onContract;
    if (fn) fn();
  };

  const checkImageValidity = () => {
    if (!onValidImageDetermined) return;

    const imgEl = img.current;
    if (imgEl && imgEl.naturalWidth > 0) {
      onValidImageDetermined(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  const handleImageLoad = () => {
    checkImageValidity();
  };

  const handleImageError = () => {
    if (onValidImageDetermined) {
      onValidImageDetermined(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!onValidImageDetermined) return;

    timeoutRef.current = setTimeout(() => {
      onValidImageDetermined(false);
      timeoutRef.current = null;
    }, 5000);

    checkImageValidity();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { label, icon, handleClick } = expanded
    ? {
        label: contractAriaLabel,
        icon: <IconContract themed={false} />,
        handleClick: handleContract,
      }
    : {
        label: expandAriaLabel,
        icon: <IconExpand themed={false} />,
        handleClick: handleExpand,
      };

  return (
    <div
      className={styles.container}
      style={{ ...(expanded ? dimensions.expanded : dimensions.contracted) }}
      onTransitionEnd={handleTransitionEnd}
    >
      <img
        src={src}
        className={styles.img}
        ref={img}
        alt={alt || expandAriaLabel}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      <button
        className={styles.button}
        aria-label={label}
        onClick={handleClick}
      >
        {icon}
      </button>
    </div>
  );
};

export default ExpandableImage;
